// GA4 reporting — cached read of GET /v2/ga4/:client_id.
// Refresh is a deliberate POST. Missing metrics render as unmeasured, never
// as a fabricated zero. Sessions, users, key events and purchase revenue
// are labelled separately.
import { useEffect, useState } from 'react';
import { ga4API } from '@/lib/api';
import { formatCount, formatMoney, formatRate, metricDisplay } from '@/lib/ga4InsightsHelpers';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { Chip, EmptyState, Loading, PageHeader, PrimaryButton, SecondaryButton, Section, Stat } from '../ui';

function MetricCard({ label, metric, formatter, testId, definition }) {
  const display = metricDisplay(metric, formatter);
  return (
    <div data-testid={testId} data-state={metric?.state || 'unmeasured'}>
      <Stat value={display.text} label={label} />
      <p className="mt-1 text-[12px] text-[var(--text-tertiary)] max-w-[28ch]">{definition}</p>
      {display.note ? <p className="mt-1 text-[12px] text-[var(--text-secondary)] max-w-[32ch]">{display.note}</p> : null}
    </div>
  );
}

function BreakdownTable({ title, testId, dimensionKey, dimensionLabel, payload }) {
  const block = payload || { available: false, rows: [] };
  return (
    <Section eyebrow={title} testId={testId}>
      {!block.available ? (
        <p className="text-[14px] text-[var(--text-secondary)] max-w-[62ch]" data-testid={`${testId}-unavailable`}>
          {block.reason || 'No rows were returned for this breakdown.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <thead>
              <tr className="text-[12px] text-[var(--text-tertiary)]">
                <th className="py-2 font-medium">{dimensionLabel}</th>
                <th className="py-2 font-medium">Sessions</th>
                <th className="py-2 font-medium">Active users</th>
                <th className="py-2 font-medium">Engaged sessions</th>
                <th className="py-2 font-medium">Key events</th>
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, index) => (
                <tr key={`${row[dimensionKey]}-${index}`} data-testid={`${testId}-row`} className="border-t border-[var(--border)]">
                  <td className="py-2 pr-3">{row[dimensionKey]}</td>
                  <td className="py-2 tabular-nums">{metricDisplay(row.sessions).text}</td>
                  <td className="py-2 tabular-nums">{metricDisplay(row.active_users).text}</td>
                  <td className="py-2 tabular-nums">{metricDisplay(row.engaged_sessions).text}</td>
                  <td className="py-2 tabular-nums">{metricDisplay(row.conversions).text}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {block.truncated ? (
            <p className="mt-2 text-[12.5px] text-[var(--text-tertiary)]">Showing a bounded page of rows. More may exist in this window.</p>
          ) : null}
        </div>
      )}
    </Section>
  );
}

export default function AnalyticsPage() {
  const { workspaceId, displayName, loadingClients } = useFounderWorkspace();
  const fp = useFounderPath();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!workspaceId) return undefined;
    let cancelled = false;
    ga4API.get(workspaceId)
      .then((res) => { if (!cancelled) setPayload(res.data); })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'Google Analytics could not be read right now.');
      });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const refresh = async () => {
    if (!workspaceId) return;
    setRefreshing(true);
    try {
      const res = await ga4API.refresh(workspaceId);
      setPayload(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Google Analytics could not be refreshed right now.');
    } finally {
      setRefreshing(false);
    }
  };

  if (loadingClients || (workspaceId && !payload && !error)) return <Loading label="Reading Analytics…" />;
  if (!workspaceId) {
    return (
      <div data-testid="analytics-page">
        <EmptyState title="No workspace yet" body="Analytics appears once a brand workspace is connected." />
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="analytics-page">
        <EmptyState testId="analytics-error" title="Google Analytics could not be read" body={typeof error === 'string' ? error : 'Try again shortly.'}
          action={<SecondaryButton to={fp('/evidence/connections')} testId="analytics-open-connections">Open Connections</SecondaryButton>} />
      </div>
    );
  }
  if (payload.state !== 'ready') {
    const prior = payload.prior_report && payload.overview;
    return (
      <div data-testid="analytics-page">
        <EmptyState testId="analytics-unavailable" title="Google Analytics is not ready"
          body={payload.message || 'Connect Google Analytics to see sessions and landing pages.'}
          action={(
            <div className="flex flex-wrap gap-2">
              <SecondaryButton to={fp('/evidence/connections')} testId="analytics-open-connections">Open Connections</SecondaryButton>
              {payload.state === 'not_synced' ? (
                <PrimaryButton onClick={refresh} disabled={refreshing} testId="analytics-refresh">
                  {refreshing ? 'Refreshing…' : 'Refresh Analytics'}
                </PrimaryButton>
              ) : null}
            </div>
          )} />
        {prior ? (
          <Section eyebrow="Previously retrieved" testId="analytics-prior-report">
            <p className="text-[13px] text-[var(--text-secondary)] max-w-[62ch]" data-testid="analytics-prior-note">
              {payload.freshness?.note || 'These figures were previously retrieved and are not a current authorized read.'}
            </p>
            <p className="mt-2 text-[12.5px] text-[var(--text-tertiary)]">
              Last successful retrieval {payload.lifecycle?.last_successful_retrieval || payload.retrieved_at || 'not recorded'}
            </p>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <MetricCard label="Sessions" metric={payload.overview.sessions} formatter={formatCount} definition="" testId="analytics-prior-sessions" />
              <MetricCard label="Active users" metric={payload.overview.active_users} formatter={formatCount} definition="" testId="analytics-prior-users" />
              <MetricCard label="Key events / conversions" metric={payload.overview.conversions} formatter={formatCount} definition="" testId="analytics-prior-conversions" />
            </div>
          </Section>
        ) : null}
      </div>
    );
  }

  const window = payload.window || {};
  const freshness = payload.freshness || {};
  const overview = payload.overview || {};
  const defs = payload.metric_definitions || {};
  const brand = displayName || 'this brand';
  const previous = payload.previous_overview;

  return (
    <div data-testid="analytics-page">
      <PageHeader
        eyebrow="Google Analytics"
        title="Analytics"
        summary={`Sessions, engagement and key events Google Analytics 4 reported for ${brand}. These are property metrics from the connected Analytics property, not Search Console queries, not ad spend, and not Shopify sales.`}
        secondary={<SecondaryButton to={fp('/evidence/connections')} testId="analytics-connections">Connections</SecondaryButton>}
        primary={<PrimaryButton onClick={refresh} disabled={refreshing} testId="analytics-refresh">{refreshing ? 'Refreshing…' : 'Refresh'}</PrimaryButton>}
      />

      <Section eyebrow="Reporting window" testId="analytics-window">
        <p className="text-[14.5px] text-[var(--text-primary)]" data-testid="analytics-dates">
          {window.start_date} to {window.end_date}
        </p>
        <p className="mt-1.5 text-[13px] text-[var(--text-secondary)] max-w-[62ch]">{freshness.note}</p>
        {freshness.stale ? (
          <p className="mt-2 text-[13px] text-[var(--text-secondary)]" data-testid="analytics-stale">
            This snapshot is older than six hours. Refresh to retrieve a current window.
          </p>
        ) : null}
        <p className="mt-2 text-[12.5px] text-[var(--text-tertiary)]">
          Last retrieved {freshness.retrieved_at || 'not recorded'} · data through {payload.data_through || 'unknown'}
          {payload.property_id ? ` · property ${payload.property_id}` : ''}
          {freshness.last_sync_status ? ` · last sync ${freshness.last_sync_status}` : ''}
        </p>
        <p className="mt-1 text-[12.5px] text-[var(--text-tertiary)]" data-testid="analytics-comparison">
          Date comparison: {payload.comparison_available ? 'available for totals' : 'not available'}. {payload.comparison_reason}
        </p>
      </Section>

      <Section eyebrow="Totals" testId="analytics-overview">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <MetricCard label="Sessions" metric={overview.sessions} formatter={formatCount} definition={defs.sessions} testId="analytics-metric-sessions" />
          <MetricCard label="Active users" metric={overview.active_users} formatter={formatCount} definition={defs.active_users} testId="analytics-metric-users" />
          <MetricCard label="Engaged sessions" metric={overview.engaged_sessions} formatter={formatCount} definition={defs.engaged_sessions} testId="analytics-metric-engaged" />
          <MetricCard label="Engagement rate" metric={overview.engagement_rate} formatter={formatRate} definition={defs.engagement_rate} testId="analytics-metric-engagement" />
          <MetricCard label="Key events / conversions" metric={overview.conversions} formatter={formatCount} definition={defs.conversions} testId="analytics-metric-conversions" />
          <MetricCard label="Purchase revenue" metric={overview.purchase_revenue} formatter={formatMoney} definition={defs.purchase_revenue} testId="analytics-metric-revenue" />
        </div>
        {previous ? (
          <p className="mt-4 text-[12.5px] text-[var(--text-tertiary)]" data-testid="analytics-previous">
            Previous window sessions {metricDisplay(previous.sessions).text}
            {payload.previous_window ? ` (${payload.previous_window.start_date} to ${payload.previous_window.end_date})` : ''}.
          </p>
        ) : null}
      </Section>

      <BreakdownTable title="Landing pages" testId="analytics-landing" dimensionKey="landingPage" dimensionLabel="Landing page" payload={payload.landing_pages} />
      <BreakdownTable title="Channels" testId="analytics-channels" dimensionKey="sessionDefaultChannelGroup" dimensionLabel="Channel" payload={payload.channels} />
      <BreakdownTable title="Source / medium" testId="analytics-source" dimensionKey="sessionSourceMedium" dimensionLabel="Source / medium" payload={payload.source_medium} />

      <Section eyebrow="Source" testId="analytics-source-note">
        <Chip>Google Analytics 4</Chip>
        <p className="mt-2 text-[13px] text-[var(--text-secondary)] max-w-[62ch]">
          Sessions are not users. Key events are not revenue. Purchase revenue is only shown when Analytics returned it for this property.
        </p>
      </Section>
    </div>
  );
}
